import { useState, useCallback, useMemo } from "react";
import { createApiService, fetchWithAuth } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { toastHandler } from "@/lib/ToastHandler";
import { formatUserName } from "@/lib/utils";

// Global cache and request deduplication
const userDataCache = new Map<string, { data: User; timestamp: number }>();
const pendingRequests = new Map<string, Promise<User | null>>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

// User interface based on the API structure
export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string | "+503 7012 3456";
  role?: string | "admin";
  userType?: string; // Campo que contiene el tipo de usuario real
  brand_ids?: number[] | [1];
  brands?: Array<{ id: number; name: string }>; // Formato del servidor
  roleSpecificData?: {
    brands?: Array<{ id: number; name: string }>;
    picture?: string; // Campo para imagen de perfil
    [key: string]: any;
  }; // Datos específicos del rol del usuario
  picture?: string; // Campo para imagen de perfil
  active?: boolean;
  created_at?: string | "2024-01-01 00:00:00";
  updated_at?: string | "2024-01-01 00:00:00";
}

// Interface for user creation
export interface CreateUserData {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string | "+503 7012 3456";
  role?: string | "admin";
  brand_ids?: number[] | [1];
  branch_ids?: number[];
}

// Interface for user update - actualizado para incluir más campos según el ejemplo de curl
export interface UpdateUserData {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string | "+503 7012 3456";
  role?: string | "admin";
  brand_ids?: number[] | [1];
  active?: boolean;
  picture?: string; // Campo para imagen en base64
  profileImage?: string;
  address?: string;
  document?: string;
  speed?: number;
  location_lon?: number;
  location_lat?: number;
  available?: boolean;
  driver_active?: boolean;
  vehicle_type?: string;
  worker_type?: string;
  branch_id?: number;
}

export function useUsersApi() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getAccessToken, setUserNickname } = useAuth();
  // Wrap getAccessToken to add error handling
  const getTokenWithErrorHandling = useCallback(async () => {
    try {
      const token = await getAccessToken();
      return token;
    } catch (error) {
      console.error("Error getting access token:", error);
      setError(
        "Error de autenticación. Por favor, intente iniciar sesión nuevamente.",
      );
      throw error;
    }
  }, [getAccessToken]);

  // Create users API service with custom endpoints - memoized to prevent recreation
  const usersService = useMemo(
    () =>
      createApiService<User>("/auth/users", getTokenWithErrorHandling, {
        // Use custom endpoint for user creation with invitation
        create: "/auth/users/create-with-invitation",
      }),
    [getTokenWithErrorHandling],
  );

  // Fetch all users - memoized to prevent unnecessary re-renders
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      await getTokenWithErrorHandling();

      const data = await usersService.getAll();

      setUsers(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Error al obtener usuarios");
      toastHandler({
        description: "Error al obtener usuarios",
        variant: "destructive",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  }, [getTokenWithErrorHandling, usersService]);

  // Helper function to check if cached data is still valid
  const isCacheValid = (timestamp: number): boolean => {
    return Date.now() - timestamp < CACHE_DURATION;
  };

  // Helper function to retry with exponential backoff
  const retryWithBackoff = async (
    fn: () => Promise<any>,
    maxRetries: number = 3,
    baseDelay: number = 1000,
  ): Promise<any> => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        // If it's a 429 (rate limit) error and we have retries left
        if (error?.status === 429 && attempt < maxRetries - 1) {
          const delay = baseDelay * Math.pow(2, attempt);

          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }
  };

  // Get user profile by email with caching and request deduplication
  const getUserByEmail = useCallback(
    async (email: string): Promise<User | null> => {
      // Check if we have valid cached data
      const cachedEntry = userDataCache.get(email);
      if (cachedEntry && isCacheValid(cachedEntry.timestamp)) {
        return cachedEntry.data;
      }

      // Check if there's already a pending request for this email
      const pendingRequest = pendingRequests.get(email);
      if (pendingRequest) {
        return pendingRequest;
      }

      // Create new request with deduplication
      const requestPromise = retryWithBackoff(
        async () => {
          try {
            const token = await getTokenWithErrorHandling();
            const userData = await fetchWithAuth(
              `/auth/user-profile?email=${encodeURIComponent(email)}`,
              token,
            );
            // Cache the successful result
            if (userData) {
              userDataCache.set(email, {
                data: userData,
                timestamp: Date.now(),
              });
            }

            return userData;
          } catch (error: any) {
            // Don't show toast for rate limiting errors during retries
            if (error?.status !== 429) {
              console.error("Error fetching user by email:", error);
              toastHandler({
                description:
                  error instanceof Error
                    ? error.message
                    : "Error al obtener el perfil del usuario. Intente nuevamente.",
                variant: "destructive",
                title: "Error",
              });
            }
            throw error;
          }
        },
        3,
        1000,
      );

      // Store the pending request to prevent duplicates
      pendingRequests.set(email, requestPromise);

      try {
        const result = await requestPromise;
        return result;
      } finally {
        // Always clean up the pending request
        pendingRequests.delete(email);
      }
    },
    [getTokenWithErrorHandling],
  );

  // Function to clear cache for a specific user (useful after updates)
  const clearUserCache = useCallback((email: string) => {
    userDataCache.delete(email);
    pendingRequests.delete(email);
  }, []);

  // Function to clear all user cache (useful for logout or major updates)
  const clearAllUserCache = useCallback(() => {
    userDataCache.clear();
    pendingRequests.clear();
  }, []);

  // Create a new user with invitation
  const createUser = async (userData: CreateUserData) => {
    try {
      const newUser = await usersService.create(userData);
      setUsers((prev) =>
        [...prev, newUser].sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(
            `${b.first_name} ${b.last_name}`,
          ),
        ),
      );
      toastHandler({
        description: `Usuario creado exitosamente`,
        title: "Éxito",
        variant: "success",
      });
      return newUser;
    } catch (error) {
      console.error("Error creating user:", error);
      toastHandler({
        description:
          error instanceof Error
            ? error.message
            : "Error al crear el usuario. Intente nuevamente.",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };

  // Update a user
  const updateUser = async (id: number, userData: UpdateUserData) => {
    try {
      const updatedUser = await usersService.update(id, userData);
      const formattedName = formatUserName(
        userData.first_name,
        userData.last_name,
      );
      if (formattedName) {
        setUserNickname(formattedName);
      }

      // Update local state
      setUsers((prev) =>
        prev.map((user) =>
          user.id === id ? { ...user, ...updatedUser } : user,
        ),
      );

      // Clear cache for this user - handle case where users array might be empty
      const userToUpdate = users.find((u) => u.id === id);

      // If we can't find the user in local state, try to find them in cache by ID
      let originalEmail: string | undefined = userToUpdate?.email;
      if (!originalEmail) {
        // Search through cache to find user with matching ID
        for (const [email, cachedEntry] of userDataCache.entries()) {
          if (cachedEntry.data.id === id) {
            originalEmail = email;
            break;
          }
        }
      }

      // Clear cache for original email if we found it
      if (originalEmail) {
        clearUserCache(originalEmail);
      }

      // Always clear cache for the new email if it was provided and is different
      if (userData.email && userData.email !== originalEmail) {
        clearUserCache(userData.email);
      }

      // If we couldn't determine the original email and email is being updated,
      // clear all cache as a safety measure to prevent stale data
      if (!originalEmail && userData.email) {
        clearAllUserCache();
      }

      toastHandler({
        description: `Usuario actualizado exitosamente`,
        title: "Éxito",
        variant: "success",
      });
      return updatedUser;
    } catch (error) {
      console.error("Error updating user:", error);
      toastHandler({
        description:
          error instanceof Error
            ? error.message
            : "Error al actualizar el usuario. Intente nuevamente.",
        variant: "destructive",
        title: "Error",
      });
      throw error;
    }
  };

  return {
    users,
    loading,
    error,
    fetchUsers,
    getUserByEmail,
    createUser,
    updateUser,
    clearUserCache,
    clearAllUserCache,
    refetchUsers: fetchUsers, // Alias para refrescar los usuarios
  };
}
